import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Mail, Key, Lock, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { passwordApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: success
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Ingresá tu email');
      return;
    }

    setLoading(true);
    try {
      await passwordApi.forgotPassword(email);
      toast.success('Si el email existe, recibirás un código');
      setStep(2);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al enviar código');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!code || code.length !== 6) {
      toast.error('Ingresá el código de 6 dígitos');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      await passwordApi.resetPassword(email, code, newPassword);
      toast.success('Contraseña actualizada correctamente');
      setStep(3);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Código inválido o expirado');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src="https://customer-assets.emergentagent.com/job_318b698c-05e0-45f0-aa51-d3d773434be0/artifacts/p0qxibr4_BETO%20FISCHER%20%282%29.svg"
              alt="Bravo Producciones"
              className="h-20 mx-auto"
            />
          </Link>
        </div>

        {/* Card */}
        <div className="bg-[#121212] border border-[#27272A] p-8" data-testid="forgot-password-page">
          <AnimatePresence mode="wait">
            {/* Step 1: Request Code */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="text-[#D4AF37]" size={32} />
                  </div>
                  <h1 className="text-2xl font-black uppercase tracking-tight text-white">
                    Recuperar Contraseña
                  </h1>
                  <p className="text-[#A1A1AA] text-sm mt-2">
                    Ingresá tu email y te enviaremos un código
                  </p>
                </div>

                <form onSubmit={handleRequestCode} className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                      Email
                    </Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="input-bravo mt-2"
                      data-testid="forgot-email-input"
                      autoFocus
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-primary py-6"
                    data-testid="send-code-btn"
                  >
                    {loading ? (
                      <Loader2 size={20} className="animate-spin" />
                    ) : (
                      'Enviar Código'
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <Link 
                    to="/login" 
                    className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2"
                  >
                    <ArrowLeft size={16} />
                    Volver a iniciar sesión
                  </Link>
                </div>
              </motion.div>
            )}

            {/* Step 2: Enter Code & New Password */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Key className="text-[#D4AF37]" size={32} />
                  </div>
                  <h1 className="text-2xl font-black uppercase tracking-tight text-white">
                    Verificar Código
                  </h1>
                  <p className="text-[#A1A1AA] text-sm mt-2">
                    Enviamos un código de 6 dígitos a<br/>
                    <span className="text-white">{email}</span>
                  </p>
                </div>

                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                      Código de Verificación
                    </Label>
                    <Input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="input-bravo mt-2 text-center text-2xl tracking-[0.5em] font-mono"
                      maxLength={6}
                      data-testid="reset-code-input"
                      autoFocus
                    />
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                      Nueva Contraseña
                    </Label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      className="input-bravo mt-2"
                      data-testid="new-password-input"
                    />
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                      Confirmar Contraseña
                    </Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repetir contraseña"
                      className="input-bravo mt-2"
                      data-testid="confirm-password-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-primary py-6"
                    data-testid="reset-password-btn"
                  >
                    {loading ? (
                      <Loader2 size={20} className="animate-spin" />
                    ) : (
                      'Cambiar Contraseña'
                    )}
                  </Button>
                </form>

                <div className="mt-6 flex justify-between">
                  <button 
                    onClick={() => setStep(1)}
                    className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2"
                  >
                    <ArrowLeft size={16} />
                    Cambiar email
                  </button>
                  <button 
                    onClick={handleRequestCode}
                    disabled={loading}
                    className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                  >
                    Reenviar código
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Success */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="text-green-500" size={40} />
                </div>
                <h1 className="text-2xl font-black uppercase tracking-tight text-white mb-2">
                  ¡Listo!
                </h1>
                <p className="text-[#A1A1AA] mb-8">
                  Tu contraseña fue actualizada correctamente
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="btn-primary px-8 py-6"
                  data-testid="goto-login-btn"
                >
                  Iniciar Sesión
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Help text */}
        <p className="text-center text-[#52525B] text-xs mt-6">
          ¿No recibiste el código? Revisá tu carpeta de spam o{' '}
          <a 
            href="mailto:central@bravoproductora.com" 
            className="text-[#D4AF37] hover:underline"
          >
            contactanos
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
